import React, { useState, useEffect } from "react";
import { Button, Form, Table } from "react-bootstrap";
import { Autocomplete, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { MecordColors } from "Constants/Color";
import {
  autocompleteDropdownStyles,
  getDisabledInputStyles,
} from "Constants/Style";
import AddButton from "Common/Components/Buttons/AddButton";
import { fetchModelByDropDown } from "slices/Masters/model/thunk";
import { fetchProductsDropDownByModel } from "slices/Masters/product/thunk";
import { fetchSacCodeByDropDown } from "slices/Masters/sac-code/thunk";
import NoSelectedProducts from "assets/images/Order/NoSelectedProducts.png";

interface EnquiryServiceFormProps {
  onAdd: (values: any) => void;
  items: any[];
  setItems: React.Dispatch<React.SetStateAction<any[]>>;
  company: any; // Added company prop
  setShowAddressModal: React.Dispatch<React.SetStateAction<boolean>>;
  formik: any; // Added formik prop
  onAddNewService?: () => void;
}

type ServiceFormValues = {
  model: string;
  model_name: string;
  serviceName: string;
  serialNo: string;
  serviceCategory: string;
  serviceMethod: string;
  serviceType: string;
  executionMode: string;
  productQuantity: string;
  serviceRate: string; // Renamed from serviceAmount
  totalServiceAmount: string; // New field
  serviceAddress: any;
  reportedComplaint: string;
  // Additional fields for payload
  category: string;
  category_name: string;
  sub_category: string;
  sub_category_name: string;
  make: string;
  make_name: string;
  unit: string;
  unit_name: string;
  item: string;
  item_name: string;
  sku: string;
  product_sku: string;
  hsn: string;
  hsn_code: string;
  sac: string;
  sac_code: string;
  gst_percent: number;
  gst_percentage: number;
  product_type: any[];
  [key: string]: any;
};

const commonInputStyle = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "13px",
};

const EnquiryServiceForm: React.FC<EnquiryServiceFormProps> = ({
  onAdd,
  items,
  setItems,
  company,
  setShowAddressModal,
  formik,
  onAddNewService,
}) => {
  const dispatch = useDispatch<any>();

  const initialFormState: ServiceFormValues = {
    model: "",
    model_name: "",
    serviceName: "",
    serialNo: "",
    serviceCategory: "",
    serviceMethod: "",
    serviceType: "",
    executionMode: "",
    productQuantity: "1",
    serviceRate: "",
    totalServiceAmount: "", // Initial empty
    serviceAddress: null,
    reportedComplaint: "",
    category: "",
    category_name: "",
    sub_category: "",
    sub_category_name: "",
    make: "",
    make_name: "",
    unit: "",
    unit_name: "",
    item: "",
    item_name: "",
    sku: "",
    product_sku: "",
    hsn: "",
    hsn_code: "",
    sac: "",
    sac_code: "",
    gst_percent: 0,
    gst_percentage: 0,
    product_type: [],
  };

  const [formData, setFormData] = useState<ServiceFormValues>(initialFormState);
  const [modelOptions, setModelOptions] = useState<any[]>([]);
  const [serviceOptions, setServiceOptions] = useState<any[]>([]);
  const [selectedModel, setSelectedModel] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [editableRowId, setEditableRowId] = useState<string | number | null>(
    null
  );
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [serviceAddressOptions, setServiceAddressOptions] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [sacCodeOptions, setSacCodeOptions] = useState<any[]>([]);
  const [selectedSacCode, setSelectedSacCode] = useState<any>(null);

  // Fetch SAC Code options on component mount
  useEffect(() => {
    dispatch(fetchSacCodeByDropDown())
      .unwrap()
      .then((res: any) => {
        setSacCodeOptions(res?.data || []);
      })
      .catch((err: any) => {
        console.error("Failed to fetch SAC options:", err);
      });
  }, [dispatch]);

  // Load Service Address Options based on Company
  useEffect(() => {
    if (
      company &&
      company.shipping_addresses &&
      Array.isArray(company.shipping_addresses)
    ) {
      const formattedOptions = company.shipping_addresses.map((addr: any) => ({
        id: addr.shipping_address_id,
        name: `${addr.address}, ${addr.city}, ${addr.state}, ${addr.country} - ${addr.pincode}`,
        address: addr,
      }));
      setServiceAddressOptions(formattedOptions);
    } else {
      // Fallback or empty if no company selected
      setServiceAddressOptions([]);
    }
  }, [company]);

  // Reset form when company changes
  useEffect(() => {
    handleFormReset();
  }, [company?.company_id]);

  // Validation Schema
  const validationSchema = Yup.object().shape({
    model: Yup.string().required("Model is required"),
    serviceName: Yup.string().required("Service is required"),
    serialNo: Yup.string().optional(),
    sac_code: Yup.string().required("SAC Code is required"),
    serviceCategory: Yup.string().required("Service Category is required"),

    serviceMethod: Yup.string().when("serviceCategory", {
      is: (val: string) => ["RMA", "Testing", "Repair"].includes(val),
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("Service Method is required"),
    }),

    serviceType: Yup.string().when("serviceCategory", {
      is: (val: string) =>
        ["RMA", "Testing", "Repair", "Installation", "Others"].includes(val),
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("Service Type is required"),
    }),

    executionMode: Yup.string()
      .when("serviceCategory", {
        is: (val: string) => ["RMA", "Testing"].includes(val),
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required("Execution Mode is required"),
      }),

    productQuantity: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .required("Product Quantity is required")
      .positive("Quantity must be positive")
      .typeError("Quantity must be a number"),
    serviceRate: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .required("Service Rate is required")
      .min(0, "Rate cannot be negative")
      .typeError("Rate must be a number"),
    serviceAddress: Yup.object().required("Service Address is required"),
  });

  // Hardcoded dropdown options for services
  const serviceCategoryOptions = [
    { id: 1, name: "Calibration" },
    { id: 2, name: "Calibration & Repair" },
    { id: 3, name: "Repair" },
    { id: 4, name: "Testing" },
    { id: 5, name: "Installation" },
    { id: 6, name: "RMA" },
    { id: 7, name: "Others" },
  ];

  const serviceMethodOptions = [
    { id: 1, name: "Offsite" },
    { id: 2, name: "Onsite" },
  ];

  const serviceTypeOptions = [
    { id: 1, name: "Standard" },
    { id: 2, name: "NABL" },
  ];

  const executionModeOptions = [
    { id: 1, name: "Inhouse" },
    { id: 2, name: "3rd party" },
  ];

  // Fetch Models on Mount
  useEffect(() => {
    dispatch(fetchModelByDropDown())
      .unwrap()
      .then((res: any) => {
        setModelOptions(res?.data || []);
      })
      .catch((err: any) => {
        console.error("Error fetching models:", err);
        toast.error("Failed to fetch models");
      });
  }, [dispatch]);

  const handleModelSelect = (_: any, selectedModel: any | null) => {
    setSelectedModel(selectedModel);

    if (!selectedModel) {
      handleFormReset();
      return;
    }

    const model_id = selectedModel.model_id;

    setFormData((prev) => ({
      ...prev,
      model: model_id,
      model_name: selectedModel.model_name,
      serviceName: "",
      item: "",
      item_name: "",
    }));

    setSelectedService(null);
    setServiceOptions([]);

    setLoadingProducts(true)
    dispatch(fetchProductsDropDownByModel(model_id))
      .unwrap()
      .then((payload: any) => {
        const services = Array.isArray(payload.data) ? payload.data : [];
        setServiceOptions(services);
      })
      .catch(() => toast.error("Error fetching services for selected model"))
      .finally(() => setLoadingProducts(false));
  };

  const handleServiceSelect = (_: any, selectedService: any) => {
    setSelectedService(selectedService);

    if (selectedService) {
      setFormData((prev) => ({
        ...prev,
        item:
          selectedService.id ||
          selectedService.product_id ||
          selectedService.item_id ||
          "",
        serviceName:
          selectedService.product_name || selectedService.item_name || "",
        item_name:
          selectedService.product_name || selectedService.item_name || "",
        // serviceRate: NO AUTOFILL requested.
        // Resetting to empty or keeping previous?
        // "add service rate and it will be editable not autofill" implies start blank.
        serviceRate: "",
        totalServiceAmount: "",
        category:
          selectedService.category?.category_id ||
          selectedService.category?.category ||
          "",
        category_name: selectedService.category?.category_name || "",
        sub_category:
          selectedService.sub_category?.sub_category_id ||
          selectedService.sub_category?.sub_category ||
          "",
        sub_category_name:
          selectedService.sub_category?.sub_category_name || "",
        make: selectedService.make?.make_id || "",
        make_name: selectedService.make?.make_name || "",
        model: selectedService.model?.model_id || prev.model,
        model_name: selectedService.model?.model_name || prev.model_name,
        hsn: selectedService.hsn_id || selectedService.hsn?.hsn_code_id || "",
        hsn_code: selectedService.hsn?.hsn_code || "",
        unit: selectedService.unit?.unit_id || selectedService.unit?.unit || "",
        unit_name: selectedService.unit?.unit_name || "",
        sku:
          selectedService.sku ||
          selectedService.item_sku ||
          selectedService.product_sku ||
          "",
        product_sku:
          selectedService.product_sku ||
          selectedService.sku ||
          selectedService.item_sku ||
          "",
        gst_percent:
          selectedService.gst_percent ||
          selectedService.hsn?.gst?.gst_percentage ||
          0,
        gst_percentage:
          selectedService.hsn?.gst?.gst_percentage ||
          selectedService.gst_percent ||
          0,
        product_type: selectedService.product_types || [],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        item: "",
        serviceName: "",
        item_name: "",
        serviceRate: "",
        totalServiceAmount: "",
        category: "",
        category_name: "",
        sub_category: "",
        sub_category_name: "",
        make: "",
        make_name: "",
        hsn: "",
        hsn_code: "",
        unit: "",
        unit_name: "",
        sku: "",
        product_sku: "",
        gst_percent: 0,
        gst_percentage: 0,
        product_type: [],
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // Calculate Total Service Amount if Quantity or Rate changes
      if (name === "productQuantity" || name === "serviceRate") {
        const qty =
          parseFloat(
            name === "productQuantity" ? value : prev.productQuantity
          ) || 0;
        const rate =
          parseFloat(name === "serviceRate" ? value : prev.serviceRate) || 0;
        updated.totalServiceAmount = (qty * rate).toFixed(2);
      }

      return updated;
    });

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFormReset = () => {
    setFormData(initialFormState);
    setServiceOptions([]);
    setSelectedModel(null);
    setSelectedService(null);
    setSelectedSacCode(null);
    setErrors({});
  };

  const handleSubmit = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});

      const payload: any = {
        ...formData,
        id: Date.now(),
        type: "service",
      };

      if (!payload.item_name) {
        payload.item_name = formData.serviceName || formData.item_name || "";
      }

      const qty = parseFloat(payload.productQuantity) || 0;
      const rate = parseFloat(payload.serviceRate) || 0;
      payload.total = (qty * rate).toFixed(2);
      payload.total_price = payload.total;
      payload.quantity = payload.productQuantity;
      payload.unit_price = payload.serviceRate; // Mapped from serviceRate

      onAdd(payload);
      toast.success("Service added successfully!");
      // handleFormReset();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path] = error.message;
          }
        });
        setErrors(newErrors);
        toast.error("Please fill all required fields correctly.");
      }
    }
  };

  const handleDelete = (index: number) => {
    const updated = [...items];
    updated.splice(index, 1);
    setItems(updated);
    toast.success("Service deleted successfully!");
  };

  const handleItemUpdate = (index: number, field: string, value: any) => {
    const updatedItems = [...items];
    const updatedItem = {
      ...updatedItems[index],
      [field]: value,
    };

    if (field === "productQuantity" || field === "serviceRate") {
      const qty =
        parseFloat(
          field === "productQuantity"
            ? value
            : updatedItem.productQuantity || updatedItem.quantity
        ) || 0;
      const rate =
        parseFloat(
          field === "serviceRate"
            ? value
            : updatedItem.serviceRate || updatedItem.unit_price
        ) || 0;
      const total = (qty * rate).toFixed(2);

      updatedItem.totalServiceAmount = total;
      updatedItem.total = total;
      updatedItem.total_price = total;
    }

    updatedItems[index] = updatedItem;
    setItems(updatedItems);
  };

  const handleAddNewAddress = () => {
    setShowAddressModal(true);
  };

  return (
    <div
      // className="p-3 border rounded bg-opacity-10 mb-4"
      className="p-2 rounded bg-opacity-10 "
      style={{
        background: MecordColors.ActiveBackground
      }}
    >
      <Form onSubmit={(e) => e.preventDefault()}>
        {/* Row 1: Model, Service, Serial No */}
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-3">
            <Form.Group>
              <Form.Label>
                Model <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={modelOptions}
                getOptionLabel={(option: any) => option?.model_name || ""}
                value={selectedModel}
                onChange={handleModelSelect}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt?.model_name || ""
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Model"
                    variant="outlined"
                    size="small"
                    error={!!errors.model}
                    helperText={errors.model}
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      ...getDisabledInputStyles(false),
                    }}
                  />
                )}
              />
            </Form.Group>
          </div>

          <div className="col-12 col-md-3">
            <Form.Group>
              <Form.Label>
                Product <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={serviceOptions}
                getOptionLabel={(option: any) => option?.product_name || ""}
                loading={loadingProducts}
                value={selectedService}
                onChange={handleServiceSelect}
                disabled={!selectedModel}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt?.product_name || ""
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Service"
                    variant="outlined"
                    size="small"
                    error={!!errors.serviceName}
                    helperText={errors.serviceName}
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      ...getDisabledInputStyles(!selectedModel),
                    }}
                  />
                )}
              />
            </Form.Group>
          </div>

          <div className="col-12 col-md-3">
            <Form.Group>
              <Form.Label style={errors.sac_code ? { color: "red" } : {}}>
                SAC Code <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={sacCodeOptions}
                getOptionLabel={(option: any) =>
                  typeof option === "string" ? option : option.sac_code || ""
                }
                value={selectedSacCode}
                onChange={(event, newValue: any) => {
                  setSelectedSacCode(newValue);
                  setFormData((prev) => ({
                    ...prev,
                    sac: newValue?.sac_code || "",
                    sac_code: newValue?.sac_code || "",
                  }));
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => (typeof opt === "string" ? opt : opt.sac_code || "")
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select SAC Code"
                    variant="outlined"
                    size="small"
                    error={!!errors.sac_code}
                    helperText={errors.sac_code}
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      ...getDisabledInputStyles(false),
                    }}
                  />
                )}
              />
            </Form.Group>
          </div>
          <div className="col-12 col-md-3">
            <Form.Group>
              <Form.Label>
                Serial No.
              </Form.Label>
              <Form.Control
                type="text"
                name="serialNo"
                value={formData.serialNo}
                onChange={handleInputChange}
                placeholder="Enter Serial No."
                style={commonInputStyle}
                className="form-control-placeholder"
                isInvalid={!!errors.serialNo}
              />
              {errors.serialNo && (
                <small className="text-danger">{errors.serialNo}</small>
              )}
            </Form.Group>
          </div>

        </div>

        {/* Row 2: Service Category, Method, Type, Execution Mode */}
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-6 col-lg-3">
            <Form.Group>
              <Form.Label>
                Service Category <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={serviceCategoryOptions}
                getOptionLabel={(option: any) => option.name || ""}
                value={
                  serviceCategoryOptions.find(
                    (s) => s.name === formData.serviceCategory
                  ) || null
                }
                onChange={(event, newValue: any) => {
                  const newCategory = newValue?.name || "";

                  // Reset fields based on new category to avoid invalid states
                  let newMethod = "";
                  let newType = "";
                  let newMode = "";

                  // Logic to pre-set or clear fields based on category
                  if (newCategory === "Installation") {
                    newMethod = "Onsite"; // Force Onsite
                    newType = ""; // Hide Type
                    // Mode stays empty or user selects
                  } else if (newCategory === "Repair") {
                    newMethod = ""; // Hide Method
                    newType = ""; // Hide Type
                    // Don't auto-fill mode - user selects
                  } else if (
                    newCategory === "RMA" ||
                    newCategory === "Testing"
                  ) {
                    // Hide all
                    newMethod = "NA";
                    newType = "NA";
                    newMode = "NA";
                  } else if (newCategory === "Others") {
                    newType = ""; // Hide Type
                  }

                  // If we are hiding them, maybe we should set them to a default "N/A" or empty string if validation allows.
                  // Based on validation schema, they are required. So we might need to adjust validation or auto-fill "N/A".
                  // For now, I will clear them. If they are hidden, they shouldn't trigger validation error?
                  // *Correction*: If hidden, I must ensure validation doesn't block.
                  // But since I can't easily change the hook schema dynamically without re-render,
                  // I will set them to "N/A" or a dummy value if hidden, OR update validation schema.
                  // Simple approach: Set to "Standard" / "Offsite" / "Inhouse" etc if hidden, or a specific "N/A" value.
                  // Let's check the options.

                  setFormData((prev) => ({
                    ...prev,
                    serviceCategory: newCategory,
                    serviceMethod: newMethod,
                    serviceType: newType,
                    executionMode: newMode,
                  }));

                  if (errors.serviceCategory) {
                    setErrors((prev) => ({ ...prev, serviceCategory: "" }));
                  }
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.name
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Service Category"
                    variant="outlined"
                    size="small"
                    error={!!errors.serviceCategory}
                    helperText={errors.serviceCategory}
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      ...getDisabledInputStyles(false),
                    }}
                  />
                )}
              />
            </Form.Group>
          </div>

          {/* Logic for Visibility */}
          {(() => {
            const { serviceCategory, serviceType } = formData;

            const isRmaOrTesting =
              serviceCategory === "RMA" || serviceCategory === "Testing";
            const isRepair = serviceCategory === "Repair";
            const isInstallation = serviceCategory === "Installation";
            const isOther = serviceCategory === "Others";
            const isCalibrationOrRepair =
              serviceCategory === "Calibration" ||
              serviceCategory === "Calibration & Repair";

            // Visibility Rules
            // 1. Calibration: Method (Offsite/Onsite) â†’ Type (Standard/NABL) â†’ Mode (Inhouse or 3rd party)
            // 2. Repair: Mode (Inhouse or 3rd party) - no Method, no Type
            // 3. Calibration & Repair: Same as Calibration
            // 4. Installation: Method (Onsite) â†’ Mode (Inhouse or 3rd party) - no Type
            // 5. Others: Method (Offsite/Onsite) â†’ Mode (Inhouse or 3rd party) - no Type
            // 6. Testing: Hide all
            // 7. RMA: Hide all

            // Default: Show all
            const showMethod = !isRmaOrTesting && !isRepair;
            const showType =
              !isRmaOrTesting && !isRepair && !isInstallation && !isOther;

            // Show mode for all except RMA/Testing
            const showMode = !isRmaOrTesting;

            // Filter Options
            let currentMethodOptions = serviceMethodOptions;
            if (isInstallation) {
              currentMethodOptions = serviceMethodOptions.filter(
                (o) => o.name === "Onsite"
              );
            }

            // All categories show both Inhouse and 3rd party options
            let currentModeOptions = executionModeOptions;

            return (
              <>
                {showMethod && (
                  <div className="col-12 col-md-6 col-lg-3">
                    <Form.Group>
                      <Form.Label>
                        Service Method <span className="text-danger">*</span>
                      </Form.Label>
                      <Autocomplete
                        options={currentMethodOptions}
                        getOptionLabel={(option: any) => option.name || ""}
                        value={
                          serviceMethodOptions.find(
                            (s) => s.name === formData.serviceMethod
                          ) || null
                        }
                        onChange={(event, newValue: any) => {
                          setFormData((prev) => ({
                            ...prev,
                            serviceMethod: newValue?.name || "",
                          }));
                          if (errors.serviceMethod) {
                            setErrors((prev) => ({
                              ...prev,
                              serviceMethod: "",
                            }));
                          }
                        }}
                        ListboxProps={autocompleteDropdownStyles.ListboxProps}
                        renderOption={(props, option) =>
                          autocompleteDropdownStyles.renderOption(
                            props,
                            option,
                            (opt) => opt.name
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Method"
                            variant="outlined"
                            size="small"
                            error={!!errors.serviceMethod}
                            helperText={errors.serviceMethod}
                            sx={{
                              ...autocompleteDropdownStyles.inputSx,
                              ...getDisabledInputStyles(false),
                            }}
                          />
                        )}
                      />
                    </Form.Group>
                  </div>
                )}

                {showType && (
                  <div className="col-12 col-md-6 col-lg-3">
                    <Form.Group>
                      <Form.Label>
                        Service Type <span className="text-danger">*</span>
                      </Form.Label>
                      <Autocomplete
                        options={serviceTypeOptions}
                        getOptionLabel={(option: any) => option.name || ""}
                        value={
                          serviceTypeOptions.find(
                            (s) => s.name === formData.serviceType
                          ) || null
                        }
                        onChange={(event, newValue: any) => {
                          const newType = newValue?.name || "";

                          // Don't clear mode when changing type - keep current selection
                          setFormData((prev) => ({
                            ...prev,
                            serviceType: newType,
                            // Keep execution mode as is
                          }));

                          if (errors.serviceType) {
                            setErrors((prev) => ({ ...prev, serviceType: "" }));
                          }
                        }}
                        ListboxProps={autocompleteDropdownStyles.ListboxProps}
                        renderOption={(props, option) =>
                          autocompleteDropdownStyles.renderOption(
                            props,
                            option,
                            (opt) => opt.name
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Type"
                            variant="outlined"
                            size="small"
                            error={!!errors.serviceType}
                            helperText={errors.serviceType}
                            sx={{
                              ...autocompleteDropdownStyles.inputSx,
                              ...getDisabledInputStyles(false),
                            }}
                          />
                        )}
                      />
                    </Form.Group>
                  </div>
                )}

                {showMode && (
                  <div className="col-12 col-md-6 col-lg-3">
                    <Form.Group>
                      <Form.Label>
                        Execution Mode <span className="text-danger">*</span>
                      </Form.Label>
                      <Autocomplete
                        options={currentModeOptions}
                        getOptionLabel={(option: any) => option.name || ""}
                        value={
                          executionModeOptions.find(
                            (s) => s.name === formData.executionMode
                          ) || null
                        }
                        onChange={(event, newValue: any) => {
                          setFormData((prev) => ({
                            ...prev,
                            executionMode: newValue?.name || "",
                          }));
                          if (errors.executionMode) {
                            setErrors((prev) => ({
                              ...prev,
                              executionMode: "",
                            }));
                          }
                        }}
                        ListboxProps={autocompleteDropdownStyles.ListboxProps}
                        renderOption={(props, option) =>
                          autocompleteDropdownStyles.renderOption(
                            props,
                            option,
                            (opt) => opt.name
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Mode"
                            variant="outlined"
                            size="small"
                            error={!!errors.executionMode}
                            helperText={errors.executionMode}
                            sx={{
                              ...autocompleteDropdownStyles.inputSx,
                              ...getDisabledInputStyles(false),
                            }}
                          />
                        )}
                      />
                    </Form.Group>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Row 3: Product Quantity, Service Rate, Total Service Amount, Service Address */}
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-6 col-lg-3">
            <Form.Group>
              <Form.Label>
                Product Quantity <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                name="productQuantity"
                value={formData.productQuantity}
                onChange={handleInputChange}
                placeholder="Default (1)"
                style={commonInputStyle}
                className="form-control-placeholder"
                isInvalid={!!errors.productQuantity}
              />
              {errors.productQuantity && (
                <small className="text-danger">{errors.productQuantity}</small>
              )}
            </Form.Group>
          </div>

          <div className="col-12 col-md-6 col-lg-3">
            <Form.Group>
              <Form.Label>
                Service Rate <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                name="serviceRate"
                value={formData.serviceRate}
                onChange={handleInputChange}
                placeholder="Enter Rate"
                style={commonInputStyle}
                className="form-control-placeholder"
                isInvalid={!!errors.serviceRate}
              />
              {errors.serviceRate && (
                <small className="text-danger">{errors.serviceRate}</small>
              )}
            </Form.Group>
          </div>

          <div className="col-12 col-md-6 col-lg-3">
            <Form.Group>
              <Form.Label>
                Total Amount <span className="text-secondary">(Auto)</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="totalServiceAmount"
                value={formData.totalServiceAmount}
                readOnly
                placeholder="0.00"
                style={{ ...commonInputStyle, backgroundColor: "#e9ecef" }}
                className="form-control-placeholder"
              />
            </Form.Group>
          </div>

          <div className="col-12 col-md-6 col-lg-3">
            <Form.Group>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <Form.Label>
                  Service Address <span className="text-danger">*</span>
                </Form.Label>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{
                    color: "red",
                    fontSize: "12px",
                    padding: "0 8px",
                    border: "none",
                    background: "transparent",
                  }}
                  onClick={() => {
                    if (!formik.values.company) {
                      toast.error("Please select customer first");
                      return;
                    }
                    handleAddNewAddress();
                  }}
                >
                  <i className="bi bi-plus-circle" /> Add New
                </button>
              </div>
              <Autocomplete
                options={serviceAddressOptions}
                getOptionLabel={(option: any) => option.name || ""}
                value={formData.serviceAddress}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, newValue: any) => {
                  setFormData((prev) => ({
                    ...prev,
                    serviceAddress: newValue, // ðŸ‘ˆ store FULL option
                  }));

                  if (errors.serviceAddress) {
                    setErrors((prev) => ({ ...prev, serviceAddress: "" }));
                  }
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.name
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Address"
                    variant="outlined"
                    size="small"
                    error={!!errors.serviceAddress}
                    helperText={errors.serviceAddress}
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      ...getDisabledInputStyles(false),
                    }}
                  />
                )}
              />
            </Form.Group>
          </div>
        </div>

        {/* Row 4: Reported Complaint + Add Button */}
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-10">
            <Form.Group>
              <Form.Label>Reported Complaint</Form.Label>
              <Form.Control
                as="textarea"
                name="reportedComplaint"
                value={formData.reportedComplaint}
                onChange={handleInputChange}
                placeholder="Enter Complaint"
                rows={1}
                style={commonInputStyle}
                className="form-control-placeholder"
              />
            </Form.Group>
          </div>

          <div className="col-12 col-md-2 d-flex align-items-end">
            <AddButton
              onClick={handleSubmit}
              label="Add"
              height="38px"
              fullWidth={true}
            />
          </div>
        </div>
      </Form>

      {/* Table */}
      {items.length > 0 ? (
        <div
          className="table-responsive"
          style={{
            background: "#FFFFFF",
            border: "1px solid #DDE1EF",
            borderRadius: "5px",
          }}
        >
          <Table className="table align-middle mb-0">
            <thead
              style={{
                backgroundColor: MecordColors?.LightGrayColor,
                color: "#fff",
              }}
            >
              <tr>
                <th style={{ minWidth: "80px" }}>Sr No.</th>
                <th style={{ minWidth: "200px" }}>Model</th>
                <th style={{ minWidth: "200px" }}>Product</th>
                <th style={{ minWidth: "150px" }}>Serial No.</th>
                <th style={{ minWidth: "150px" }}>SAC Code</th>
                <th style={{ minWidth: "150px" }}>Service Category</th>
                <th style={{ minWidth: "150px" }}>Service Method</th>
                <th style={{ minWidth: "150px" }}>Service Type</th>
                <th style={{ minWidth: "150px" }}>Execution Mode</th>
                <th style={{ minWidth: "150px" }}>Product Quantity</th>
                {/* <th style={{ minWidth: "100px" }}>Unit</th> */}
                <th style={{ minWidth: "150px" }}>Service Rate</th>
                <th style={{ minWidth: "170px" }}>Total Service Amount</th>
                <th style={{ minWidth: "250px" }}>Service Address </th>
                <th style={{ minWidth: "170px" }}>Reported Complaint </th>
                <th
                  style={{
                    minWidth: "100px",
                    position: "sticky",
                    right: 0,
                    zIndex: 5,
                    backgroundColor: MecordColors?.LightGrayColor,
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item: any, index: number) => {
                const rowId = item.product_id;
                // const isEditable = editableRowId === rowId;
                return (
                  <tr key={item.product_id}>
                    <td>{index + 1}</td>
                    <td>{item?.model_name || item?.serviceName}</td>
                    <td>{item?.product_name || item?.serviceName}</td>
                    <td>{item?.serialNo || "-"}</td>
                    <td>{item?.sac_code || item?.sac || "-"}</td>
                    <td>{item?.serviceCategory || "-"}</td>
                    <td>{item?.serviceMethod || "-"}</td>
                    <td>{item?.serviceType || "-"}</td>
                    <td>{item?.executionMode || "-"}</td>
                    <td>{item?.productQuantity || item?.quantity}</td>

                    {/* Quantity Editable */}
                    {/* <td>
                                            <Form.Control
                                                type="number"
                                                min="1"
                                                value={item.productQuantity || item.quantity}
                                                // Always editable
                                                onChange={(e) =>
                                                    handleItemUpdate(index, "productQuantity", e.target.value)
                                                }
                                                size="sm"
                                                style={commonInputStyle}
                                                className="form-control-placeholder"
                                            />
                                        </td> */}

                    {/* <td>{item?.unit_name || item?.unit || "-"}</td> */}

                    {/* Service Rate Editable */}
                    <td>
                      <div className="d-flex align-items-center">
                        <span className="me-1">{"\u20B9"}</span>
                        <Form.Control
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={item.serviceRate} // Use serviceRate
                          // Always editable
                          onChange={(e) =>
                            handleItemUpdate(
                              index,
                              "serviceRate",
                              e.target.value
                            )
                          }
                          size="sm"
                          style={{ ...commonInputStyle, paddingLeft: "5px" }}
                          className="form-control-placeholder"
                        />
                      </div>
                    </td>

                    {/* Total */}
                    <td>
                      {"\u20B9"}{" "}
                      {item.totalServiceAmount ||
                        (
                          Number(item.productQuantity || item.quantity) *
                          Number(item.serviceRate)
                        ).toFixed(2)}
                    </td>
                    <td>
                      {item.shipping_address &&
                        typeof item.shipping_address === "object" &&
                        item.shipping_address.address ? (
                        `${item.shipping_address.address}, ${item.shipping_address.city}, ${item.shipping_address.state}, ${item.shipping_address.country} - ${item.shipping_address.pincode}`
                      ) : item.shipping_address ? (
                        item.shipping_address
                      ) : item.serviceAddress?.name ? (
                        item.serviceAddress.name
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{item.reportedComplaint}</td>

                    {/* Actions */}
                    <td
                      className="text-center"
                      style={{
                        position: "sticky",
                        right: 0,
                        background: "white",
                        zIndex: 5,
                      }}
                    >
                      <i
                        className="bi bi-trash text-danger"
                        style={{ cursor: "pointer" }}
                        title="Delete Service"
                        onClick={() => handleDelete(index)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="text-center text-muted py-4 border rounded p-3 bg-white">
          <img
            src={NoSelectedProducts}
            alt="No services selected"
            className="mb-3"
            style={{ maxWidth: "180px", height: "auto" }}
          />
          <div>Search existing services to add to this list</div>
        </div>
      )}

      {formik.errors.item_details && formik.touched.item_details && (
        <div
          className="text-danger"
          style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}
        >
          {typeof formik.errors.item_details === "string"
            ? formik.errors.item_details
            : "At least one service is required"}
        </div>
      )}
    </div>
  );
};

export default EnquiryServiceForm;
